import { Button } from "@repo/ui/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@repo/ui/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@repo/ui/components/ui/popover";
import { Plus } from "lucide-react";
import { useState } from "react";
import type { DraftMemberReference } from "./types";

export interface SubscoreCandidate {
  key: string;
  reference: DraftMemberReference;
  label: string;
  /** Why the question cannot be scored; offered disabled rather than hidden, so the author learns why. */
  reason?: string;
}

/** Stays open after a pick, so several questions can be added in one go. */
export function SubscoreQuestionPicker({
  scoreLabel,
  candidates,
  onPick,
}: {
  scoreLabel: string;
  candidates: SubscoreCandidate[];
  onPick: (reference: DraftMemberReference) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 gap-1 px-2 text-xs text-muted-foreground"
            aria-label={`Add question to ${scoreLabel || "score"}`}
          />
        }
      >
        <Plus className="size-3" />
        Add question
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="start">
        <Command>
          <CommandInput placeholder="Search questions..." />
          <CommandList>
            <CommandEmpty>No questions left to add</CommandEmpty>
            <CommandGroup>
              {candidates.map((candidate) => (
                <CommandItem
                  key={candidate.key}
                  value={`${candidate.label} ${candidate.key}`}
                  disabled={!!candidate.reason}
                  onSelect={() => onPick(candidate.reference)}
                  className="flex-col items-start gap-0.5"
                >
                  <span className="line-clamp-2">{candidate.label}</span>
                  {candidate.reason && <span className="text-xs text-muted-foreground">{candidate.reason}</span>}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
